/**
 * Button Component
 * Phase 5: Code Quality - Reusable button component
 *
 * BEFORE: Button styles duplicated across 15+ components
 * AFTER: Single Button component with consistent styling and behavior
 */

import React from 'react';
import { LoadingSpinner } from '../LoadingSpinner';
import '../../styles/shared/Button.css';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'success' | 'ghost';
export type ButtonSize = 'small' | 'medium' | 'large';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Button variant
   * @default 'primary'
   */
  variant?: ButtonVariant;

  /**
   * Button size
   * @default 'medium'
   */
  size?: ButtonSize;

  /**
   * Whether button is in loading state
   * @default false
   */
  loading?: boolean;

  /**
   * Icon to display before label
   */
  icon?: React.ReactNode;

  /**
   * Icon to display after label
   */
  iconAfter?: React.ReactNode;

  /**
   * Full width button
   * @default false
   */
  fullWidth?: boolean;

  /**
   * Button children (label)
   */
  children: React.ReactNode;
}

/**
 * Button Component
 *
 * Provides consistent button styling and behavior across the application.
 *
 * @example
 * ```tsx
 * // Primary button
 * <Button onClick={handleClick}>Save</Button>
 *
 * // Danger button with loading state
 * <Button variant="danger" loading={isDeleting}>
 *   Delete
 * </Button>
 *
 * // Button with icon
 * <Button icon={<PlusIcon />} variant="success">
 *   Add Control
 * </Button>
 * ```
 */
export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  loading = false,
  icon,
  iconAfter,
  fullWidth = false,
  disabled,
  className = '',
  children,
  ...props
}) => {
  const classes = [
    'af-button',
    `af-button--${variant}`,
    `af-button--${size}`,
    fullWidth && 'af-button--full-width',
    loading && 'af-button--loading',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      {...props}
      className={classes}
      disabled={disabled || loading}
      aria-disabled={disabled || loading}
      aria-busy={loading}
    >
      {loading && (
        <span className="af-button__spinner">
          <LoadingSpinner
            size={size === 'small' ? 12 : size === 'large' ? 18 : 14}
            color="currentColor"
          />
        </span>
      )}
      {!loading && icon && <span className="af-button__icon">{icon}</span>}
      <span className="af-button__label">{children}</span>
      {!loading && iconAfter && <span className="af-button__icon-after">{iconAfter}</span>}
    </button>
  );
};

export default Button;
